<template>
  <div class="collection-details-container">
    <div class="collection-actions">
      <BbBtn
        size="x-small"
        type="icon"
        icon="chevron-left"
        icon-size="small"
        no-background
        class="back-button"
        @click="$emit('navigate')"
      />
      <div class="collection-actions-right">
        <BbBtn v-if="!isReadOnly(collection)" size="small" type="secondary" @click="editCollection">
          <BbIcon size="small" name="edit" />
          <TextBody i18n-key="common.edit" tag="span" />
        </BbBtn>
        <BbBtn v-if="!isReadOnly(collection)" size="small" type="secondary" @click="shareCollection">
          <BbIcon size="small" name="share" />
          <TextBody i18n-key="common.share" tag="span" />
        </BbBtn>
        <ButtonDeleteSidepanelItems :count="collectionDocuments.length" @on-click="openDeletePopup" />
      </div>
    </div>

    <GenericContentComponent title-key="collections.whatIsInside">
      <div class="shared-info-message" v-if="isReadOnly(collection)">
        <BbIcon size="small" name="information" type="filled" />
        <TextBody tag="span" i18n-key="collections.sharedCollection.sharedCollectionInfoMessage" :body-type="5" />
      </div>
      <span v-if="collection?.collectionName" class="collection-title">{{ collection?.collectionName || '' }}</span>
      <span v-if="collection?.collectionDescription" class="collection-description">{{
        collection?.collectionDescription || ''
      }}</span>
      <div class="owner" v-if="isReadOnly(collection)">
        <TextBody tag="span" i18n-key="collections.sharedCollection.ownedBy" :body-type="1" color="#fff" />
        <span class="collection-title">{{ ownerData }}</span>
      </div>
      <div class="collection-details">
        <span>{{ documentsCount }}</span>
        <span class="bullet">•</span>
        <span>{{ formatUpdatedAt }}</span>
      </div>
    </GenericContentComponent>

    <div class="documents-list" v-if="!isDataLoading">
      <FavoriteDocumentCard
        v-for="(reference, index) in collectionDocuments"
        :key="reference.externalId || reference"
        :collection-id="collectionId"
        :is-read-only="isReadOnly(collection)"
        :reference="reference"
        :index="index"
      />
    </div>
    <CardsListSkeletonLoader v-else />
    <WarningPopup
      :popup-opened="confirmDeletePopupOpened"
      title-key="collections.deleteCollection"
      warning-message-key="collections.collectionsWarning"
      confirm-text-key="modals.confirmDeleteText"
      @update:open="confirmDeletePopupOpened = false"
      @execute:action="confirmDelete"
    />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';

import useModals from '~/composables/useModals';
import useAuthData from '~/composables/useAuthData';
import { calculatePassedTime } from '~/utils/date-utils';

import TextBody from '~/components/common/TextBody';
import useCollections from '~/composables/useCollections';
import FavoriteDocumentCard from '~/components/conversation/favoritesPanel/FavoriteDocumentCard';
import CardsListSkeletonLoader from '~/components/conversation/sidepanelGeneralComponents/CardsListSkeletonLoader';
import WarningPopup from '~/components/common/WarningPopup';
import ButtonDeleteSidepanelItems from '~/components/conversation/sidepanelGeneralComponents/ButtonDeleteSidepanelItems.vue';
import GenericContentComponent from '~/components/conversation/sidepanelGeneralComponents/GenericContentComponent.vue';

export default {
  components: {
    GenericContentComponent,
    ButtonDeleteSidepanelItems,
    TextBody,
    FavoriteDocumentCard,
    CardsListSkeletonLoader,
    WarningPopup,
  },
  props: {
    collectionId: {
      type: String,
      default: '',
    },
    collectionsCount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['navigate'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { setAdditionalDocumentsData, getCollection, removeCollection, isReadOnly } = useCollections();
    const { setCreateCollectionPopup, setSharedLinkCollectionPopup } = useModals();
    const { getUserData } = useAuthData();

    const isDataLoading = ref(false);
    const confirmDeletePopupOpened = ref(false);
    const ownerData = ref('');

    const collection = computed(() => getCollection(props.collectionId).value);
    const collectionDocuments = computed(() => collection.value?.documents || []);
    const formatUpdatedAt = computed(() => calculatePassedTime(collection.value?.modifiedAt || ''));
    const documentsCount = computed(
      () => `${(collection.value && collection.value.documents?.length) || 0}  ${t('collections.documents')}`,
    );

    const editCollection = () => setCreateCollectionPopup({ open: true, collection: collection.value });
    const shareCollection = () => setSharedLinkCollectionPopup({ open: true, collectionId: props.collectionId });
    const openDeletePopup = () => {
      if (collection.value.documents.length) {
        confirmDeletePopupOpened.value = true;
      } else {
        removeCollection(props.collectionId);
        emit('navigate');
      }
    };
    const confirmDelete = () => {
      removeCollection(props.collectionId);
      confirmDeletePopupOpened.value = false;
      emit('navigate');
    };

    // need this in order to reload data when user chooses another collection
    watch(
      () => props.collectionId,
      async (newId, oldId) => {
        if (newId && newId !== oldId) {
          ownerData.value = '';
          isDataLoading.value = true;
          await setAdditionalDocumentsData(newId);
          isDataLoading.value = false;
          if (isReadOnly(collection.value)) {
            const { name = '', nickname = '', email = '' } = (await getUserData(collection.value.owner)) || {};
            ownerData.value = name || nickname || email;
          }
        }
      },
      { immediate: true },
    );

    return {
      confirmDelete,
      confirmDeletePopupOpened,
      collection,
      collectionDocuments,
      documentsCount,
      editCollection,
      formatUpdatedAt,
      isDataLoading,
      isReadOnly,
      openDeletePopup,
      ownerData,
      removeCollection,
      shareCollection,
    };
  },
};
</script>

<style scoped lang="scss">
.collection-details-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .shared-info-message {
    display: flex;
    gap: $bb-spacing-2;
    padding: $bb-spacing-2;
    border-radius: $bb-spacing-5;
    border: 1px solid $bb-neutral-4;

    :deep(span) {
      color: $bb-neutral-0 !important;
    }

    i {
      color: #ffd853;
    }
  }

  .collection-title {
    @include bb-regular-text-styles('md');
    font-weight: 500;
    color: $bb-neutral-0;
  }

  .collection-description {
    @include bb-regular-text-styles('md');
    color: $bb-neutral-0;
  }

  .owner {
    span {
      color: $bb-neutral-0 !important;
    }
  }

  .collection-details {
    @include bb-regular-text-styles('xs');

    span {
      color: $bb-neutral-3;
      letter-spacing: 0.1px;
      font-size: 13px;
    }

    .bullet {
      margin: 0 $bb-spacing-1;
    }
  }

  .collection-actions {
    padding: $bb-spacing-2 $bb-spacing-4;
    background-color: $bb-neutral-0;
    width: 100%;
    display: flex;
    gap: $bb-spacing-1;
    align-items: center;

    .collection-actions-right {
      display: flex;
      gap: $bb-spacing-1;
      align-items: center;
      width: 100%;

      button {
        flex: 1 !important;

        :deep(.v-btn__content) {
          display: flex;
          gap: $bb-spacing-1;
        }
      }
    }

    .back-button {
      background-color: $bb-neutral-6;
      height: 36px !important;
      width: 48px !important;
      padding: 10px 16px;
      border-radius: 100px !important;
    }
  }

  .documents-list {
    display: flex;
    flex-direction: column;

    & > div {
      padding: $bb-spacing-3 $bb-spacing-4;

      &:not(:last-of-type) {
        border-bottom: 1px solid $bb-neutral-4;
      }
    }
  }
}
</style>
